import React, { useState, useEffect } from 'react';
import API from '../../services/api';
import { THEME } from '../../constants/theme';

const NewComplaint = ({ onNavigate }) => {
    const [formData, setFormData] = useState({
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        complaint_type: 'Vehicle_Issue',
        priority: 'Medium',
        subject: '',
        description: '',
        assigned_department: 'Customer_Care',
        bike_master_id: null
    });

    const [customerSearch, setCustomerSearch] = useState('');
    const [customerResults, setCustomerResults] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [recentCustomers, setRecentCustomers] = useState([]);

    useEffect(() => {
        fetchRecentCustomers();
    }, []);

    const fetchRecentCustomers = async () => {
        try {
            const res = await API.get('/customer-care/customers/recent?limit=5');
            setRecentCustomers(res.data.data || []);
        } catch (err) {
            console.log('Recent customers not loaded');
        }
    };

    const searchCustomers = async (query) => {
        if (query.length < 3) {
            setCustomerResults([]);
            return;
        }
        setSearching(true);
        try {
            const res = await API.get(`/customer-care/customers/search?query=${encodeURIComponent(query)}`);
            setCustomerResults(res.data.data || []);
        } catch (err) {
            console.error('Customer search failed:', err);
        } finally {
            setSearching(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (customerSearch.length >= 3) searchCustomers(customerSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [customerSearch]);

    const handleCustomerSelect = (customer) => {
        setSelectedCustomer(customer);
        setFormData({
            ...formData,
            customer_id: customer.id,
            customer_name: customer.full_name,
            customer_phone: customer.phone,
            bike_master_id: customer.recent_purchase?.bike_master_id || null
        });
        setCustomerResults([]);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await API.post('/customer-care/complaints', formData);
            onNavigate('complaints');
        } catch (err) {
            alert('Error creating complaint');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.pageWrapper}>
            {/* Header Section */}
            
            <div style={styles.mainGrid}>
                {/* Left Side: Customer & Type */}
                <div style={styles.column}>
                    <section style={styles.sectionCard}>
                        <h3 style={styles.sectionTitle}>Customer Information</h3>
                        {!selectedCustomer ? (
                            <div style={styles.searchContainer}>
                                <input
                                    style={styles.input}
                                    placeholder="Search by Name, Phone or CNIC..."
                                    value={customerSearch}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                />
                                {customerResults.length > 0 && (
                                    <div style={styles.dropdown}>
                                        {customerResults.map(c => (
                                            <div key={c.id} onClick={() => handleCustomerSelect(c)} style={styles.dropdownItem}>
                                                <strong>{c.full_name}</strong>
                                                <span>{c.phone}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={styles.selectedCustomerCard}>
                                <div>
                                    <div style={styles.custName}>{selectedCustomer.full_name}</div>
                                    <div style={styles.custMeta}>{selectedCustomer.phone} | {selectedCustomer.cnic}</div>
                                </div>
                                <button onClick={() => setSelectedCustomer(null)} style={styles.textBtn}>Change</button>
                            </div>
                        )}
                    </section>

                    <section style={styles.sectionCard}>
                        <h3 style={styles.sectionTitle}>Categorization</h3>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Complaint Category</label>
                            <select 
                                style={styles.select}
                                value={formData.complaint_type}
                                onChange={(e) => setFormData({...formData, complaint_type: e.target.value})}
                            >
                                <option value="Vehicle_Issue">Vehicle Issue</option>
                                <option value="Service_Behavior">Service Behavior</option>
                                <option value="Billing">Billing & Finance</option>
                                <option value="Warranty">Warranty Claim</option>
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Priority Level</label>
                            <div style={styles.priorityGroup}>
                                {['Low', 'Medium', 'High', 'Critical'].map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setFormData({...formData, priority: p})}
                                        style={{
                                            ...styles.pillBtn,
                                            backgroundColor: formData.priority === p ? THEME.primary : '#f1f5f9',
                                            color: formData.priority === p ? '#fff' : '#475569'
                                        }}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>

                {/* Right Side: Details */}
                <div style={styles.column}>
                    <section style={styles.sectionCard}>
                        <h3 style={styles.sectionTitle}>Issue Details</h3>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Subject</label>
                            <input 
                                style={styles.input}
                                placeholder="E.g. Engine making unusual noise"
                                value={formData.subject}
                                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Description</label>
                            <textarea 
                                style={{...styles.input, minHeight: '150px', resize: 'none'}}
                                placeholder="Describe the problem in detail..."
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                            />
                        </div>
                        
                        <div style={styles.actionArea}>
                            <button 
                                onClick={handleSubmit} 
                                disabled={loading || !selectedCustomer}
                                style={{
                                    ...styles.primaryBtn,
                                    opacity: (!selectedCustomer || loading) ? 0.7 : 1
                                }}
                            >
                                {loading ? 'Processing...' : 'Submit Complaint'}
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

const styles = {
    pageWrapper: {
        padding: '32px',
        backgroundColor: '#f8fafc',
       // minHeight: '100vh',
        fontFamily: "'Inter', sans-serif"
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    mainTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0
    },
    subTitle: {
        fontSize: '14px',
        color: '#64748b',
        marginTop: '4px'
    },
    mainGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1.2fr',
        gap: '24px',
        maxWidth: '1200px'
    },
    sectionCard: {
        backgroundColor: '#ffffff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#334155',
        marginBottom: '20px',
        borderBottom: '1px solid #f1f5f9',
        paddingBottom: '12px'
    },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '500',
        color: '#475569',
        marginBottom: '8px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        outline: 'none',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box'
    },
    select: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#fff'
    },
    priorityGroup: {
        display: 'flex',
        gap: '8px'
    },
    pillBtn: {
        flex: 1,
        padding: '10px',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    primaryBtn: {
        width: '100%',
        padding: '14px',
        backgroundColor: THEME.primary,
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    secondaryBtn: {
        padding: '10px 18px',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        color: '#475569',
        fontWeight: '500',
        cursor: 'pointer'
    },
    selectedCustomerCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: '#f0f9ff',
        borderRadius: '8px',
        border: '1px solid #bae6fd'
    },
    custName: { fontWeight: '700', color: '#0369a1' },
    custMeta: { fontSize: '12px', color: '#0c4a6e' },
    textBtn: {
        background: 'none',
        border: 'none',
        color: THEME.primary,
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'underline'
    },
    dropdown: {
        position: 'absolute',
        width: '100%',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        zIndex: 10,
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
    },
    dropdownItem: {
        padding: '12px 16px',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        flexDirection: 'column'
    }
};

export default NewComplaint;